import { renderDocument } from '@archetelos/editor';
import { FREE_READ_LIMIT } from '@archetelos/lib/config';
import { formatArticleDate } from '@archetelos/lib/date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { LogoIcon } from '@/components/brand/Logo';
import { useTrackView } from '@/features/analytics/useTrackView';
import { decideArticleAccess, type ArticleAccess } from '@/features/reader/articleAccess';
import { useTimeTracking } from '@/features/reader/useTimeTracking';
import { BookmarkButton } from '@/features/subscriber/BookmarkButton';
import { useReadingPreferences } from '@/features/subscriber/useReadingPreferences';
import { useAuth } from '@/lib/auth/useAuth';
import { useAssetMap } from '@/lib/queries/assets';
import { contentQueries, type RailArticle } from '@/lib/queries/content';
import { consumeFreeRead } from '@/lib/queries/freemiumReads';
import { subscriberQueries, upsertReadingProgress } from '@/lib/queries/subscriber';

export const Route = createFileRoute('/articles/$slug')({
  component: ArticleReaderRoute,
});

function ArticleReaderRoute() {
  const { loading, user } = useAuth();
  const { slug } = Route.useParams();
  const article = useQuery(contentQueries.articleRender(slug));
  const content = article.data;
  // Resolve the body's image nodes — whatever doc the RPC returned (a gated
  // preview resolves only the assets it actually contains).
  const assetMap = useAssetMap(content?.bodyJson ?? null);
  const savedProgress = useQuery(subscriberQueries.progressFor(user?.id, content?.id));
  const related = useQuery(contentQueries.relatedArticles(content?.id));

  // Anonymous lane-1 view — no id, even for a signed-in reader (Spec 22).
  useTrackView(content?.id);

  const [progress, setProgress] = useState(0);
  const [distractionFree, setDistractionFree] = useState(false);
  const tickRef = useRef<number | null>(null);
  const flushRef = useRef<number | null>(null);
  const resumeShownRef = useRef(false);
  const prefs = useReadingPreferences();

  useTimeTracking(content?.id, Boolean(content));

  // Resume toast: once, when we have saved progress between 5% and 95%.
  useEffect(() => {
    if (resumeShownRef.current) return;
    const p = savedProgress.data;
    if (!p || p.completed) return;
    if (p.scroll_percent >= 5 && p.scroll_percent < 95) {
      resumeShownRef.current = true;
      toast(`Resuming where you left off (${p.scroll_percent}%).`, {
        action: {
          label: 'Jump there',
          onClick: () => {
            const h = document.documentElement;
            window.scrollTo({
              top: (p.scroll_percent / 100) * (h.scrollHeight - h.clientHeight),
              behavior: 'smooth',
            });
          },
        },
      });
    }
  }, [savedProgress.data]);

  // Scroll → progress bar + debounced reading_progress upsert.
  useEffect(() => {
    const onScroll = () => {
      if (tickRef.current != null) return;
      tickRef.current = window.requestAnimationFrame(() => {
        const h = document.documentElement;
        const pct = (h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight)) * 100;
        const clamped = Math.min(100, Math.max(0, pct));
        setProgress(clamped);
        tickRef.current = null;
        if (flushRef.current) window.clearTimeout(flushRef.current);
        flushRef.current = window.setTimeout(() => {
          if (user && content) {
            void upsertReadingProgress({
              userId: user.id,
              contentId: content.id,
              scrollPercent: clamped,
            }).catch(() => {});
          }
        }, 1500);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (flushRef.current) window.clearTimeout(flushRef.current);
    };
  }, [user, content]);

  if (loading || article.isLoading) {
    return <p className="p-12 text-center font-sans text-sm text-ink/50">Loading…</p>;
  }
  if (!content) {
    return (
      <p className="p-12 text-center font-sans text-sm text-ink/70">
        That article isn&apos;t available.
      </p>
    );
  }

  const access = decideArticleAccess(content, Boolean(user));

  return (
    <div className={distractionFree ? 'distraction-free' : ''}>
      <div
        role="progressbar"
        aria-label="Reading progress"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="fixed inset-x-0 top-0 z-50 h-0.5 bg-parchment"
      >
        <div className="h-full bg-oxblood" style={{ width: `${progress}%` }} />
      </div>

      <Link
        to="/"
        aria-label="ArcheTelos"
        data-df-hide
        className="fixed left-4 top-3 z-50 rounded-md border border-ink/15 bg-parchment/95 p-1.5 hover:opacity-80"
      >
        <LogoIcon className="h-6 w-6" />
      </Link>

      <div className="fixed right-4 top-3 z-50 flex items-center gap-2">
        {user && (
          <span data-df-hide className="rounded-md bg-parchment/95">
            <BookmarkButton contentId={content.id} />
          </span>
        )}
        <button
          type="button"
          onClick={() => setDistractionFree((d) => !d)}
          className="rounded-md border border-ink/15 bg-parchment/95 px-2.5 py-1 font-sans text-xs uppercase tracking-[0.08em] text-ink/70 hover:text-oxblood"
        >
          {distractionFree ? 'Show chrome' : 'Distraction-free'}
        </button>
      </div>

      <article
        className="mx-auto max-w-3xl px-6 py-16"
        // Drive the shared .prose size/leading via custom properties — a plain
        // fontSize/lineHeight here would now lose to the real .prose rule and
        // silently break the reading-preferences control (Spec 38 §2).
        style={
          {
            '--prose-font-size': `${prefs.fontScale}rem`,
            '--prose-line-height': prefs.lineHeight,
          } as React.CSSProperties
        }
      >
        <h1 className="font-serif text-4xl leading-tight text-ink balance sm:text-5xl">
          {content.title}
        </h1>
        {content.excerpt && (
          <p className="mt-6 font-serif text-xl italic text-ink/75">{content.excerpt}</p>
        )}
        <p className="mt-6 font-sans text-sm text-ink/60">
          {content.publishedAt ? formatArticleDate(new Date(content.publishedAt)) : ''}
        </p>
        {content.coverImageUrl && (
          <img
            src={content.coverImageUrl}
            alt={content.coverImageAlt ?? content.title}
            className="mt-10 w-full rounded-md"
          />
        )}
        <div className="relative">
          <div
            className="prose font-serif mt-12"
            dangerouslySetInnerHTML={{ __html: renderDocument(content.bodyJson, assetMap) }}
          />
          {access.state !== 'full' && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-parchment to-transparent" />
          )}
        </div>

        {access.state !== 'full' && (
          <ReaderWall access={access} slug={slug} contentId={content.id} />
        )}
      </article>

      {related.data && related.data.articles.length > 0 && (
        <section className="mx-auto max-w-3xl px-6 pb-16">
          <h2 className="font-sans text-sm font-medium uppercase tracking-[0.08em] text-ink/60">
            {related.data.heading}
          </h2>
          <div className="mt-2 border-t border-ink/10">
            {related.data.articles.map((a) => (
              <ParchmentArticleRow key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** A related-essay row in the reader's parchment idiom (no dark tokens). */
function ParchmentArticleRow({ article }: { article: RailArticle }) {
  return (
    <Link
      to="/articles/$slug"
      params={{ slug: article.slug }}
      className="group block border-b border-ink/10 py-4 last:border-0"
    >
      <h3 className="font-serif text-lg text-ink group-hover:text-oxblood">{article.title}</h3>
      {article.publishedAt && (
        <p className="mt-0.5 font-sans text-xs text-ink/50">
          {formatArticleDate(new Date(article.publishedAt))}
        </p>
      )}
      {article.excerpt && (
        <p className="mt-1 line-clamp-1 font-sans text-sm text-ink/70">{article.excerpt}</p>
      )}
    </Link>
  );
}

function ReaderWall({
  access,
  slug,
  contentId,
}: {
  access: ArticleAccess;
  slug: string;
  contentId: string;
}) {
  const qc = useQueryClient();
  const spend = useMutation({
    mutationFn: () => consumeFreeRead(contentId),
    onSuccess: () => {
      // Whether allowed (→ full body) or now exhausted (→ out_of_reads), the
      // render + status both need refetching to reflect the new state.
      void qc.invalidateQueries({ queryKey: ['article-render', slug] });
      void qc.invalidateQueries({ queryKey: ['freemium-reads', 'status'] });
    },
  });

  return (
    <div className="mt-2 rounded-lg border border-ink/10 bg-parchment p-6 text-center shadow-sm">
      {access.state === 'spend_prompt' && (
        <>
          <p className="font-serif text-lg text-ink">
            {access.remaining} free article{access.remaining === 1 ? '' : 's'} left this month.
            Spend one to read the rest?
          </p>
          <button
            type="button"
            onClick={() => spend.mutate()}
            disabled={spend.isPending}
            className="mt-4 rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            {spend.isPending ? 'Unlocking…' : 'Read the rest'}
          </button>
          <p className="mt-3">
            <Link to="/subscribe" className="font-sans text-sm text-oxblood hover:underline">
              Subscribe for unlimited reading →
            </Link>
          </p>
        </>
      )}

      {access.state === 'out_of_reads' && (
        <>
          <p className="font-serif text-lg text-ink">
            You&apos;ve used your {FREE_READ_LIMIT} free reads this month — they reset on the 1st.
            Subscribe to keep reading.
          </p>
          <Link
            to="/subscribe"
            className="mt-4 inline-block rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Subscribe
          </Link>
        </>
      )}

      {access.state === 'members_only' && (
        <>
          <p className="font-serif text-lg text-ink">This piece is for members.</p>
          <Link
            to="/subscribe"
            className="mt-4 inline-block rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Subscribe
          </Link>
        </>
      )}

      {access.state === 'needs_signin' && (
        <>
          <p className="font-serif text-lg text-ink">Sign in to keep reading.</p>
          <Link
            to="/auth/sign-in"
            search={{ next: `/articles/${slug}` }}
            className="mt-4 inline-block rounded-md bg-ink px-5 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
          >
            Sign in
          </Link>
          <p className="mt-3 font-sans text-sm text-ink/70">
            New here?{' '}
            <Link
              to="/auth/sign-up"
              search={{ next: `/articles/${slug}` }}
              className="text-oxblood hover:underline"
            >
              Create a free account
            </Link>{' '}
            — read this now, and more each month.
          </p>
        </>
      )}
    </div>
  );
}
